import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';

export type InputType = 'text' | 'password' | 'number' | 'multiline' | 'email';

@Component({
  selector: 'app-input',
  imports: [],
  templateUrl: './input.component.html',
  styleUrl: './input.component.css'
})
export class InputComponent implements ControlValueAccessor {
  private static idCounter = 0;

  label = input<string>();
  placeholder = input<string>();
  value = input<string>();
  type = input<InputType>('text');
  helpText = input<string>();
  errorMessage = input<string>();
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  showable = input<boolean>(false);

  protected readonly ICONS = ICONS;
  protected readonly inputId = `app-input-${++InputComponent.idCounter}`;
  protected readonly currentValue = signal('');
  protected readonly isDisabled = signal(false);
  protected readonly showValue = signal(false);
  protected readonly effectiveType = computed(() => {
    if (!this.showable()) return this.type();
    return this.showValue() ? 'text' : 'password';
  });

  private readonly ngControl: NgControl | null;
  private readonly translate = inject(TranslateService);
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.ngControl = inject(NgControl, { optional: true, self: true });
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // Sync the value input to internal state when used without CVA
    effect(() => {
      const v = this.value();
      if (v !== undefined && !this.ngControl) {
        this.currentValue.set(v);
      }
    });
  }

  writeValue(value: string | null): void {
    this.currentValue.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected hasError(): boolean {
    if (this.ngControl) {
      return !!(this.ngControl.invalid && this.ngControl.touched);
    }
    return !!this.errorMessage();
  }

  protected computedDisabled(): boolean {
    return this.isDisabled() || this.disabled();
  }

  protected getErrorsCount(): number {
    if (!this.hasError()) return 0;

    const errors = this.ngControl?.errors;
    return errors ? Object.keys(errors).length : 0;
  }

  protected getErrorMessage(): string | null {
    if (!this.hasError()) return null;

    const errors = this.ngControl?.errors;

    if (errors) {
      const errorsKeys = errors ? Object.keys(errors) : [];
      return this.translate.instant(`form.error.${errorsKeys[0]}`, errors?.[errorsKeys[0]]);
    }

    return this.errorMessage() ?? null;
  }

  protected onInput(event: Event): void {
    const val = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    this.currentValue.set(val);
    this.onChange(val);
  }

  protected onBlur(): void {
    this.onTouched();
  }
}
